import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

/**
 * FirmCandidatesTab Component
 * 
 * Shows all candidates across all pipelines for a specific firm
 */

const FirmCandidatesTab = ({ firmId }) => {
  const [candidates, setCandidates] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [filters, setFilters] = useState({
    pipeline_id: '',
    stage: '',
    search: ''
  });
  const [pipelines, setPipelines] = useState([]);

  // Load pipelines once on mount
  useEffect(() => {
    loadPipelines();
  }, [firmId]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadCandidates();
    }, 300);

    return () => clearTimeout(timer);
  }, [firmId, filters]);

  const loadPipelines = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const pipelinesResponse = await axios.get(
        `${API_URL}/pipelines?firm_id=${firmId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const firmPipelines = pipelinesResponse.data.filter(p => p.firm_id === parseInt(firmId));
      setPipelines(firmPipelines);
    } catch (err) {
      console.error('Error loading pipelines:', err);
    }
  };

  const loadCandidates = async () => {
    try {
      if (initialLoading) {
        setInitialLoading(true);
      } else {
        setSearchLoading(true);
      }

      const token = localStorage.getItem('authToken');
      
      // Build query params
      const params = new URLSearchParams({ firm_id: firmId });
      if (filters.pipeline_id) params.append('pipeline_id', filters.pipeline_id);
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.search) params.append('search', filters.search);

      // Load candidates
      const response = await axios.get(
        `${API_URL}/candidate-archive/internal?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCandidates(response.data);

    } catch (err) {
      console.error('Error loading candidates:', err);
    } finally {
      setInitialLoading(false);
      setSearchLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      withdrawn: 'bg-gray-100 text-gray-700',
      active: 'bg-blue-100 text-blue-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (initialLoading) {
    return <div className="text-center py-8">Loading candidates...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          {searchLoading && (
            <span className="text-sm text-gray-500 flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              Searching...
            </span>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              placeholder="Name, company, title..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline</label>
            <select
              value={filters.pipeline_id}
              onChange={(e) => setFilters({...filters, pipeline_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Pipelines</option>
              {pipelines.map(pipeline => (
                <option key={pipeline.id} value={pipeline.id}>{pipeline.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
            <select
              value={filters.stage}
              onChange={(e) => setFilters({...filters, stage: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Stages</option>
              <option value="Format">Format</option>
              <option value="Notes">Notes</option>
              <option value="Review">Review</option>
              <option value="Sent">Sent</option>
              <option value="Interviewing">Interviewing</option>
              <option value="Disqualified">Disqualified</option>
            </select>
          </div>
        </div>

        <button
          onClick={() => setFilters({ pipeline_id: '', stage: '', search: '' })}
          className="mt-4 text-sm text-blue-600 hover:text-blue-700"
        >
          Clear Filters
        </button>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Candidates ({candidates.length})
          </h3>
        </div>

        {candidates.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p className="text-gray-600">No candidates found</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your filters or add candidates to pipelines</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pipeline
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Added
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {candidates.map((candidate) => (
                  <tr key={candidate.card_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{candidate.candidate_name}</div>
                        <div className="text-sm text-gray-500">{candidate.current_title}</div>
                        <div className="text-sm text-gray-500">{candidate.current_company}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{candidate.pipeline_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{candidate.current_stage}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(candidate.final_status)}`}>
                        {candidate.final_status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(candidate.added_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirmCandidatesTab;